import { useCallback, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { buildCombo, esCampoEditable } from './dom';
import { KeyboardScopeContext, type KeyboardScopeApi, type KeyboardScopePriority, type RegisteredScope } from './keyboardScopeContext';

const PRIORITY_ORDER: KeyboardScopePriority[] = ['overlay', 'search-dropdown', 'table-cell', 'page-action', 'global'];

export const KeyboardScopeProvider = ({ children }: { children: ReactNode }) => {
  const scopesRef = useRef<RegisteredScope[]>([]);

  const register = useCallback((scope: RegisteredScope) => {
    scopesRef.current = [...scopesRef.current.filter((s) => s.id !== scope.id), scope];
  }, []);

  const unregister = useCallback((id: string) => {
    scopesRef.current = scopesRef.current.filter((s) => s.id !== id);
  }, []);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      const combo = buildCombo(event);
      const enEditable = esCampoEditable(event.target) || esCampoEditable(document.activeElement);

      for (const priority of PRIORITY_ORDER) {
        const scopesEnPrioridad = scopesRef.current.filter((s) => s.priority === priority);
        for (let i = scopesEnPrioridad.length - 1; i >= 0; i -= 1) {
          const scope = scopesEnPrioridad[i];
          if (enEditable && !scope.allowInEditable) continue;
          const handlerFn = scope.bindingsRef.current[combo];
          if (handlerFn) {
            event.preventDefault();
            event.stopPropagation();
            handlerFn(event);
            return;
          }
        }
      }
    };

    window.addEventListener('keydown', handler, { capture: true });
    return () => window.removeEventListener('keydown', handler, { capture: true });
  }, []);

  const api = useMemo<KeyboardScopeApi>(() => ({ register, unregister }), [register, unregister]);

  return <KeyboardScopeContext.Provider value={api}>{children}</KeyboardScopeContext.Provider>;
};
